import {
  ChangeEvent,
  FocusEvent,
  useState,
  forwardRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
} from 'react';
import type { AriaRole, ComponentProps } from 'react';
import classNames from 'classnames';
import { Icon, IconName } from '@components/icon';
import { AssistiveText } from '@components/input/AssistiveText';
import './Input.scss';

type useTogglePasswordType = (initialType?: string) => {
  passwordType: string;
  togglePasswordVisibility: () => void;
};

/**
 * Hook to toggle password visibility
 */
export const useTogglePasswordVisibility: useTogglePasswordType = (initialType = 'text') => {
  const [passwordType, setPasswordType] = useState<string>(initialType);
  const togglePasswordVisibility = () => {
    if (passwordType === 'password') {
      setPasswordType('text');
    } else {
      setPasswordType('password');
    }
  };

  return { passwordType, togglePasswordVisibility };
};

export type AllowedInputTypes = 'text' | 'password' | 'email' | 'date';

export interface InputProps
  extends Omit<ComponentProps<'input'>, 'type' | 'size' | 'value' | 'defaultValue'> {
  id: string;
  /** ARIA role forwarded to the `<input>` element. Used internally by `SelectDropdown` (searchable
   *  variant) which renders this `Input` with `role="combobox"` per WAI-ARIA combobox pattern. */
  role?: AriaRole;
  type?: AllowedInputTypes;
  value?: string;
  defaultValue?: string;
  assistiveText?: string;
  inputSize?: number;
  endIcon?: IconName;
  label?: string;
  error?: boolean;
  isPlaceholderColored?: boolean;
  suppressAutofillUi?: boolean;
}

/**
 * Input component
 *
 * @param name - Name for the input field.
 * @param disabled - If true, input is disabled.
 * @param onChange - Change callback event.
 * @param onFocus - Focus callback event.
 * @param onBlur - Blur callback event.
 * @param type - Set the input type for the component. Default "text".
 * @param assistiveText - Assistive text for the input field.
 * @param id - Id for the input field.
 * @param required - If true, input is required.
 * @param minLength - Minimum length for the input field.
 * @param maxLength - Maximum length for the input field. Default 50.
 * @param inputSize - Size for the input field.
 * @param endIcon - Icon for the input field.
 * @param label - Label for the input field.
 * @param error - If true, input gain error status.
 * @param defaultValue - Default value for the input field. Default empty string "".
 * @param autoComplete - Autocomplete for the input field. Default "off".
 * @param value - Value for the input field. Used in case you need the input the be controlled.
 * @param placeholder - Placeholder for the input field.
 * @param isPlaceholderColored - If true, the placeholder is colored like normal text instead of the default grey.
 * @param suppressAutofillUi - If true, hides Safari's built-in Contacts/Credentials AutoFill button. Default false.
 * @param className - Additional class name for the input wrapper
 * @param inputMode - Hints at the type of virtual keyboard to display. Useful for numeric-only or date fields.
 * @constructor
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      name,
      disabled,
      onChange,
      onFocus,
      onBlur,
      type = 'text',
      assistiveText,
      required,
      minLength,
      maxLength = 50,
      inputSize,
      endIcon,
      label,
      error,
      defaultValue = '',
      autoComplete = 'off',
      placeholder = '',
      isPlaceholderColored = false,
      suppressAutofillUi = false,
      className,
      inputMode,
      ...rest
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [value, setValue] = useState<string>(defaultValue);
    const { passwordType, togglePasswordVisibility } = useTogglePasswordVisibility(type);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const setInputRef = useCallback(
      (node: HTMLInputElement | null) => {
        inputRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );

    // Native `autoFocus` (and SSR/hydration timing) can leave the input focused before
    // React's `onFocus` runs, so `vds-input--active` never applies and the label stays down.
    useLayoutEffect(() => {
      const input = inputRef.current;
      if (!input || disabled) {
        return;
      }
      if (document.activeElement === input) {
        setIsFocused(true);
      }
    }, [disabled]);

    // value lands in ...rest (not destructured) so consumers can pass a controlled value
    // prop; the state sync here keeps internal styling state (vds-input--filled) in step.
    useEffect(() => {
      if (rest?.value !== undefined && rest?.value !== null && rest?.value !== value) {
        setValue(rest.value);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- setValue is safe to exclude
    }, [rest?.value]);

    const focusHandler = (e: FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      if (onFocus && !disabled) {
        onFocus(e);
      }
    };

    const blurHandler = (e: FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      if (onBlur && !disabled) {
        onBlur(e);
      }
    };

    const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
      const { target } = e;
      setValue(target.value);
      if (onChange && !disabled) {
        onChange(e);
      }
    };

    const isPasswordHidden = passwordType === 'password';

    return (
      <div
        className={classNames('vds-input', className, {
          'vds-input--filled': value || type === 'date',
          'vds-input--active': isFocused,
          'vds-input--error': error,
          'vds-input--disabled': disabled,
          'vds-input--colored-placeholder': isPlaceholderColored,
          'vds-input--no-autofill-ui': suppressAutofillUi,
        })}>
        <div
          className={classNames('vds-input__control', {
            'vds-input__control--password': type === 'password',
            'vds-input__control--with-icon': endIcon || type === 'password',
          })}>
          {label ? <label htmlFor={id}>{label}</label> : null}

          <input
            required={required}
            id={id}
            name={name}
            type={passwordType}
            value={value}
            minLength={minLength}
            maxLength={maxLength}
            size={inputSize}
            disabled={disabled}
            onChange={changeHandler}
            onBlur={blurHandler}
            onFocus={focusHandler}
            ref={setInputRef}
            aria-invalid={error || undefined}
            aria-describedby={assistiveText ? `${id}-text` : undefined}
            autoComplete={autoComplete}
            placeholder={placeholder}
            inputMode={inputMode}
            {...rest}
          />

          {endIcon && type === 'text' ? (
            <span className="vds-input__icon">
              <Icon name={endIcon} size="medium" />
            </span>
          ) : null}

          {type === 'password' ? (
            <button
              className="vds-input__icon vds-input__icon--password"
              onClick={togglePasswordVisibility}
              type="button"
              role="switch"
              aria-label={isPasswordHidden ? 'Show password' : 'Hide password'}
              aria-live="polite"
              aria-checked={!isPasswordHidden}>
              {!isPasswordHidden ? (
                <Icon name="eye-open" size="medium" />
              ) : (
                <Icon name="eye-closed" size="medium" />
              )}
            </button>
          ) : null}
        </div>

        <AssistiveText error={error} disabled={disabled} id={`${id}-text`}>
          {assistiveText}
        </AssistiveText>
      </div>
    );
  },
);
